import React, { useEffect, useState, useRef, useCallback } from 'react';
import Layout from './Layout';
import './css/CalendarPage.css';

// ===== ALERT MODAL (replaces window.alert for timetable warnings) =====
const AlertModal = ({ message, onClose }) => {
  if (!message) return null;
  return (
    <div className="cal-alert-overlay">
      <div className="cal-alert-box">
        <div className="cal-alert-icon">⚠️</div>
        <p className="cal-alert-message">{message}</p>
        <button className="cal-alert-btn" onClick={onClose}>OK</button>
      </div>
    </div>
  );
};

const CalendarPage = () => {
  const [user, setUser] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [appointments, setAppointments] = useState({});
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [viewMode] = useState('month');
  const [loading, setLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  // Branch → Doctor cascade
  const [branches, setBranches] = useState([]);
  const [doctorsByBranch, setDoctorsByBranch] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [bookedSlots, setBookedSlots] = useState([]);  // [{start_time, end_time}]
  const [isRestDay, setIsRestDay] = useState(false);
  const [workingHours, setWorkingHours] = useState('');
  const [timeConflict, setTimeConflict] = useState(''); // instant conflict message

  // Client search
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [clientSearchResults, setClientSearchResults] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientSearchLoading, setClientSearchLoading] = useState(false);
  const clientSearchRef = useRef(null);
  const searchDebounceRef = useRef(null);

  const [newAppointment, setNewAppointment] = useState({
    title: '',
    description: '',
    start_time: '',
    duration_minutes: 30,
    client_id: '',
    doctor_id: '',
    branch_id: '',
    appointment_date: ''
  });

  // ===== INIT =====
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      loadAppointments();
    }
    loadBranches();
  }, []);

  useEffect(() => {
    if (user) loadAppointments();
  }, [currentDate, user]);

  // Close client dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (clientSearchRef.current && !clientSearchRef.current.contains(e.target)) {
        setClientSearchResults([]);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ===== DATA LOADING =====
  // Loads appointments for everyone — not filtered by which user created them.
  const loadAppointments = async () => {
    try {
      setLoading(true);
      const month = currentDate.getMonth() + 1;
      const year = currentDate.getFullYear();
      const response = await fetch(`/api/calendarContent/appointments?month=${month}&year=${year}`);
      const data = await response.json();
      if (data.success) setAppointments(data.data);
    } catch (error) {
      console.error('Error loading appointments:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadBranches = async () => {
    try {
      const res = await fetch('/api/calendarContent/branches');
      const data = await res.json();
      if (data.success) setBranches(data.data);
    } catch (error) {
      console.error('Error loading branches:', error);
    }
  };

  const loadDoctorsByBranch = async (branchId) => {
    if (!branchId) { setDoctorsByBranch([]); return; }
    try {
      const res = await fetch(`/api/calendarContent/doctors-by-branch/${branchId}`);
      const data = await res.json();
      if (data.success) setDoctorsByBranch(data.data);
    } catch (error) {
      console.error('Error loading doctors:', error);
    }
  };

  const loadAvailableSlots = async (date, doctorId) => {
    if (!doctorId || !date) {
      setAvailableSlots([]); setBookedSlots([]); setIsRestDay(false); setWorkingHours(''); setTimeConflict('');
      return;
    }
    try {
      const dateString = formatDateToString(date);
      const res = await fetch(`/api/calendarContent/available-slots/${doctorId}/${dateString}`);
      const data = await res.json();
      if (data.success) {
        setAvailableSlots(data.data.availableSlots);
        setBookedSlots(data.data.bookedSlots || []);
        setIsRestDay(data.isRestDay || false);
        setWorkingHours(data.data.workingHours || '');
        setTimeConflict('');
        if (data.isRestDay) {
          setAlertMessage(data.message);
          setNewAppointment(prev => ({ ...prev, start_time: '' }));
        }
      } else {
        setAvailableSlots([]); setBookedSlots([]);
      }
    } catch (error) {
      setAvailableSlots([]); setBookedSlots([]);
    }
  };

  // Instant conflict check — called when start_time or duration_minutes changes
  const validateTimeSlot = (startTime, durationMinutes, currentBookedSlots, currentIsRestDay, excludeAppointmentId = null) => {
    if (!startTime) { setTimeConflict(''); return; }
    if (currentIsRestDay) {
      setTimeConflict('Consultant is on rest day. Please select another date.');
      return;
    }

    // Calculate end time
    const [sh, sm] = startTime.split(':').map(Number);
    const endTotalMin = sh * 60 + sm + parseInt(durationMinutes || 30);
    const endH = String(Math.floor(endTotalMin / 60)).padStart(2, '0');
    const endM = String(endTotalMin % 60).padStart(2, '0');
    const endTime = `${endH}:${endM}`;

    // Check overlap with booked slots
    const toMin = (t) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
    const newStart = toMin(startTime);
    const newEnd = toMin(endTime);

    const conflict = currentBookedSlots.find(b => {
      const bStart = toMin(b.start_time);
      const bEnd = toMin(b.end_time);
      // Overlap: new period starts before booked ends AND new period ends after booked starts
      return newStart < bEnd && newEnd > bStart;
    });

    if (conflict) {
      setTimeConflict(
        `⛔ Time conflict: Consultant already has an appointment from ${conflict.start_time} to ${conflict.end_time}. Please choose a different time or duration.`
      );
    } else {
      setTimeConflict('');
    }
  };

  // Client search with debounce
  const handleClientSearch = useCallback((q) => {
    setClientSearchQuery(q);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    if (!q.trim()) { setClientSearchResults([]); return; }
    searchDebounceRef.current = setTimeout(async () => {
      try {
        setClientSearchLoading(true);
        const res = await fetch(`/api/calendarContent/clients/search?q=${encodeURIComponent(q)}`);
        const data = await res.json();
        if (data.success) setClientSearchResults(data.data);
      } catch (err) {
        console.error('Client search error:', err);
      } finally {
        setClientSearchLoading(false);
      }
    }, 300);
  }, []);

  const selectClient = (client) => {
    setSelectedClient(client);
    setNewAppointment(prev => ({ ...prev, client_id: client.id }));
    setClientSearchQuery('');
    setClientSearchResults([]);
  };

  const clearClient = () => {
    setSelectedClient(null);
    setNewAppointment(prev => ({ ...prev, client_id: '' }));
    setClientSearchQuery('');
  };

  // ===== HELPERS =====
  const formatDateToString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDateKey = (date) => formatDateToString(date);

  const isToday = (day) => {
    if (!day) return false;
    const today = new Date();
    return day === today.getDate() &&
      currentDate.getMonth() === today.getMonth() &&
      currentDate.getFullYear() === today.getFullYear();
  };

  const isSelected = (day) => {
    if (!day) return false;
    return day === selectedDate.getDate() &&
      currentDate.getMonth() === selectedDate.getMonth() &&
      currentDate.getFullYear() === selectedDate.getFullYear();
  };

  const hasAppointments = (day) => {
    if (!day) return false;
    const dateKey = formatDateKey(new Date(currentDate.getFullYear(), currentDate.getMonth(), day));
    return appointments[dateKey] && appointments[dateKey].length > 0;
  };

  const formatTimeDisplay = (timeString) => {
    if (!timeString) return '';
    if (typeof timeString === 'string' && timeString.length >= 5) return timeString.substring(0, 5);
    return timeString;
  };

  const formatTimeRange = (startTime, endTime) => {
    const start = formatTimeDisplay(startTime);
    const end = formatTimeDisplay(endTime);
    return end ? `${start} - ${end}` : start;
  };

  const getDurationText = (minutes) => {
    if (!minutes) return '';
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  const getSelectedDateAppointments = () => {
    const dateKey = formatDateKey(selectedDate);
    return appointments[dateKey] || [];
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    for (let i = 0; i < firstDay.getDay(); i++) days.push(null);
    for (let day = 1; day <= lastDay.getDate(); day++) days.push(day);
    return days;
  };

  // ===== EVENT HANDLERS =====
  const handleDateClick = (day) => {
    if (!day) return;
    setSelectedDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), day));
  };

  const handlePrevMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  const handleNextMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));

  const resetModal = () => {
    setNewAppointment({ title: '', description: '', start_time: '', duration_minutes: 30, client_id: '', doctor_id: '', branch_id: '', appointment_date: '' });
    setAvailableSlots([]);
    setBookedSlots([]);
    setDoctorsByBranch([]);
    setIsRestDay(false);
    setWorkingHours('');
    setTimeConflict('');
    setSelectedClient(null);
    setClientSearchQuery('');
    setClientSearchResults([]);
  };

  const handleAddAppointment = () => {
    resetModal();
    const dateString = formatDateKey(selectedDate);
    setNewAppointment(prev => ({ ...prev, appointment_date: dateString }));
    setShowAppointmentModal(true);
  };

  const handleEditAppointment = async (appointment) => {
    resetModal();
    setEditingAppointment(appointment);
    const dateStr = formatDateKey(selectedDate);
    const doctorId = appointment.doctor ? appointment.doctor.id : '';

    // Set client
    setSelectedClient(appointment.client
      ? { id: appointment.client.id, name: appointment.client.name, ic: appointment.client.ic }
      : null
    );

    // Find which branch this doctor belongs to (use first branch)
    let prefillBranchId = '';
    let prefillDoctors = [];
    if (doctorId) {
      try {
        const branchRes = await fetch(`/api/calendarContent/doctor-branches/${doctorId}`);
        const branchData = await branchRes.json();
        if (branchData.success && branchData.data.length > 0) {
          prefillBranchId = String(branchData.data[0].id);
          // Load doctors for that branch
          const docRes = await fetch(`/api/calendarContent/doctors-by-branch/${prefillBranchId}`);
          const docData = await docRes.json();
          if (docData.success) prefillDoctors = docData.data;
        }
      } catch (e) {
        console.error('Failed to load doctor branch info:', e);
      }
    }

    setDoctorsByBranch(prefillDoctors);

    setNewAppointment({
      title: appointment.title,
      description: appointment.description || '',
      start_time: appointment.start_time ? formatTimeDisplay(appointment.start_time) : '',
      duration_minutes: appointment.duration_minutes || 30,
      client_id: appointment.client.id,
      doctor_id: doctorId,
      branch_id: prefillBranchId,
      appointment_date: dateStr
    });

    // Load available slots for this doctor+date
    if (doctorId) {
      const dateParts = dateStr.split('-').map(Number);
      loadAvailableSlots(new Date(dateParts[0], dateParts[1] - 1, dateParts[2]), doctorId);
    }

    setShowEditModal(true);
  };

  const handleBranchChange = (branchId) => {
    setNewAppointment(prev => ({ ...prev, branch_id: branchId, doctor_id: '', start_time: '' }));
    setAvailableSlots([]);
    setIsRestDay(false);
    setWorkingHours('');
    loadDoctorsByBranch(branchId);
  };

  const handleDoctorChange = (doctorId) => {
    setNewAppointment(prev => ({ ...prev, doctor_id: doctorId, start_time: '' }));
    setAvailableSlots([]);
    setIsRestDay(false);
    setWorkingHours('');
    if (doctorId && newAppointment.appointment_date) {
      const dateParts = newAppointment.appointment_date.split('-').map(Number);
      loadAvailableSlots(new Date(dateParts[0], dateParts[1] - 1, dateParts[2]), doctorId);
    }
  };

  const handleDateChange = (dateString) => {
    setNewAppointment(prev => ({ ...prev, appointment_date: dateString, start_time: '' }));
    setAvailableSlots([]);
    setIsRestDay(false);
    setWorkingHours('');
    if (newAppointment.doctor_id && dateString) {
      const dateParts = dateString.split('-').map(Number);
      loadAvailableSlots(new Date(dateParts[0], dateParts[1] - 1, dateParts[2]), newAppointment.doctor_id);
    }
  };

  const showAlert = (msg) => setAlertMessage(msg);

  const handleSaveAppointment = async () => {
    if (!newAppointment.title.trim()) { showAlert('Please enter an appointment title'); return; }
    if (!newAppointment.client_id) { showAlert('Please select a customer'); return; }
    if (!newAppointment.branch_id) { showAlert('Please select a branch'); return; }
    if (!newAppointment.doctor_id) { showAlert('Please select a consultant'); return; }
    if (!newAppointment.start_time) { showAlert('Please select a start time'); return; }
    if (isRestDay) { showAlert('The consultant is on rest day. Please select another date or consultant.'); return; }
    if (timeConflict) { showAlert(timeConflict); return; }

    try {
      setLoading(true);
      const res = await fetch('/api/calendarContent/appointments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...newAppointment, user_id: user.pkkey })
      });
      const data = await res.json();
      if (data.success) {
        await loadAppointments();
        resetModal();
        setShowAppointmentModal(false);
      } else {
        showAlert(data.message || 'Failed to create appointment');
      }
    } catch (error) {
      showAlert('Failed to create appointment');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAppointment = async () => {
    if (!newAppointment.title.trim()) { showAlert('Please enter an appointment title'); return; }
    if (!newAppointment.client_id) { showAlert('Please select a customer'); return; }
    if (!newAppointment.doctor_id) { showAlert('Please select a consultant'); return; }
    if (!newAppointment.start_time) { showAlert('Please select a start time'); return; }
    if (isRestDay) { showAlert('The consultant is on rest day. Please select another date or consultant.'); return; }
    if (timeConflict) { showAlert(timeConflict); return; }

    try {
      setLoading(true);
      const res = await fetch(`/api/calendarContent/appointments/${editingAppointment.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: newAppointment.title,
          description: newAppointment.description,
          client_id: newAppointment.client_id,
          doctor_id: newAppointment.doctor_id,
          start_time: newAppointment.start_time,
          duration_minutes: newAppointment.duration_minutes,
          appointment_date: newAppointment.appointment_date
        })
      });
      const data = await res.json();
      if (data.success) {
        await loadAppointments();
        setShowEditModal(false);
        setEditingAppointment(null);
        resetModal();
      } else {
        showAlert(data.message || 'Failed to update appointment');
      }
    } catch (error) {
      showAlert('Failed to update appointment');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAppointment = async (appointmentId) => {
    if (!window.confirm('Are you sure you want to delete this appointment?')) return;
    try {
      setLoading(true);
      const res = await fetch(`/api/calendarContent/appointments/${appointmentId}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) await loadAppointments();
    } catch (error) {
      console.error('Error deleting appointment:', error);
    } finally {
      setLoading(false);
    }
  };

  // ===== CONSTANTS =====
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const durationOptions = [
    { value: 15, label: '15 minutes' }, { value: 30, label: '30 minutes' },
    { value: 45, label: '45 minutes' }, { value: 60, label: '1 hour' },
    { value: 90, label: '1.5 hours' }, { value: 120, label: '2 hours' }
  ];

  if (!user) return null;

  // ===== SHARED FORM SECTIONS =====
  const renderAppointmentForm = () => (
    <>
      {/* Title */}
      <div className="form-group">
        <label>Appointment Title *</label>
        <input
          type="text"
          value={newAppointment.title}
          onChange={(e) => setNewAppointment({ ...newAppointment, title: e.target.value })}
          placeholder="Enter appointment title"
        />
      </div>

      {/* Date */}
      <div className="form-group">
        <label>Date *</label>
        <input
          type="date"
          value={newAppointment.appointment_date}
          onChange={(e) => handleDateChange(e.target.value)}
        />
      </div>

      {/* Branch */}
      <div className="form-group">
        <label>📍 Branch *</label>
        <select
          value={newAppointment.branch_id}
          onChange={(e) => handleBranchChange(e.target.value)}
        >
          <option value="">Select branch...</option>
          {branches.map(b => (
            <option key={b.id} value={b.id}>{b.name}</option>
          ))}
        </select>
      </div>

      {/* Consultant */}
      <div className="form-group">
        <label>🩺 Consultant *</label>
        <select
          value={newAppointment.doctor_id}
          onChange={(e) => handleDoctorChange(e.target.value)}
          disabled={!newAppointment.branch_id}
        >
          <option value="">
            {!newAppointment.branch_id ? 'Select branch first' : 'Select consultant...'}
          </option>
          {doctorsByBranch.map(d => (
            <option key={d.id} value={d.id}>{d.name}{d.position ? ` — ${d.position}` : ''}</option>
          ))}
        </select>
      </div>

      {/* Customer search */}
      <div className="form-group">
        <label>👤 Customer *</label>
        {selectedClient ? (
          <div className="selected-client-card">
            <div className="selected-client-info">
              <strong>{selectedClient.name}</strong>
              {selectedClient.ic && <span className="client-ic">IC: {selectedClient.ic}</span>}
            </div>
            <button type="button" className="clear-client-btn" onClick={clearClient}>✕</button>
          </div>
        ) : (
          <div className="client-search-wrapper" ref={clientSearchRef}>
            <input
              type="text"
              value={clientSearchQuery}
              onChange={(e) => handleClientSearch(e.target.value)}
              placeholder="Search by name / IC / phone / email..."
              className="client-search-input"
              autoComplete="off"
            />
            {clientSearchLoading && <div className="client-search-loading">Searching...</div>}
            {clientSearchResults.length > 0 && (
              <div className="client-search-dropdown">
                {clientSearchResults.map(c => (
                  <div key={c.id} className="client-search-item" onMouseDown={() => selectClient(c)}>
                    <strong>{c.name}</strong>
                    {c.ic && <span className="client-search-ic">IC: {c.ic}</span>}
                    {c.phone && <span className="client-search-phone">{c.phone}</span>}
                  </div>
                ))}
              </div>
            )}
            {clientSearchQuery.trim().length > 0 && !clientSearchLoading && clientSearchResults.length === 0 && (
              <div className="client-search-dropdown">
                <div className="client-search-empty">No customers found</div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Start Time */}
      <div className="form-group">
        <label>Start Time *</label>
        {isRestDay ? (
          <div className="rest-day-notice">🚫 Consultant is on rest day — no slots available</div>
        ) : (
          <>
            <select
              value={newAppointment.start_time}
              onChange={(e) => {
                const val = e.target.value;
                setNewAppointment(prev => ({ ...prev, start_time: val }));
                validateTimeSlot(val, newAppointment.duration_minutes, bookedSlots, isRestDay);
              }}
              disabled={!newAppointment.doctor_id || availableSlots.length === 0}
            >
              <option value="">
                {!newAppointment.doctor_id ? 'Select consultant first' :
                  availableSlots.length === 0 ? 'No available slots' : 'Select time'}
              </option>
              {availableSlots.map(slot => (
                <option key={slot} value={slot}>{slot}</option>
              ))}
              {editingAppointment?.start_time &&
                !availableSlots.includes(formatTimeDisplay(editingAppointment.start_time)) && (
                  <option value={formatTimeDisplay(editingAppointment.start_time)}>
                    {formatTimeDisplay(editingAppointment.start_time)} (Current)
                  </option>
                )}
            </select>
            {workingHours && (
              <small className="form-help">🕐 Working hours: {workingHours}</small>
            )}
            {timeConflict && (
              <div className="time-conflict-notice">{timeConflict}</div>
            )}
          </>
        )}
      </div>

      {/* Duration */}
      <div className="form-group">
        <label>Duration</label>
        <select
          value={newAppointment.duration_minutes}
          onChange={(e) => {
            const val = parseInt(e.target.value);
            setNewAppointment(prev => ({ ...prev, duration_minutes: val }));
            if (newAppointment.start_time) {
              validateTimeSlot(newAppointment.start_time, val, bookedSlots, isRestDay);
            }
          }}
        >
          {durationOptions.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {/* Description */}
      <div className="form-group">
        <label>Description</label>
        <textarea
          value={newAppointment.description}
          onChange={(e) => setNewAppointment({ ...newAppointment, description: e.target.value })}
          placeholder="Enter appointment description"
          rows="3"
        />
      </div>
    </>
  );

  // ===== RENDER =====
  return (
    <Layout>
      <div className="calendar-page">
        {/* Alert Modal */}
        <AlertModal message={alertMessage} onClose={() => setAlertMessage('')} />

        {/* Header */}
        <div className="calendar-header">
          <h1 className="calendar-title">Appointment Calendar</h1>
          <div className="calendar-controls">
            <button className="add-event-btn" onClick={handleAddAppointment}>
              + Add Appointment
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="calendar-content">
          <div className="calendar-main">
            <div className="calendar-navigation">
              <button className="nav-btn" onClick={handlePrevMonth}>&#8249;</button>
              <h2 className="current-month">
                {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h2>
              <button className="nav-btn" onClick={handleNextMonth}>&#8250;</button>
            </div>

            <div className="calendar-grid">
              <div className="calendar-days-header">
                {dayNames.map(day => (
                  <div key={day} className="day-header">{day}</div>
                ))}
              </div>
              <div className="calendar-days">
                {getDaysInMonth(currentDate).map((day, index) => (
                  <div
                    key={index}
                    className={`calendar-day ${day ? 'has-day' : 'empty-day'} ${isToday(day) ? 'today' : ''} ${isSelected(day) ? 'selected' : ''} ${hasAppointments(day) ? 'has-events' : ''}`}
                    onClick={() => handleDateClick(day)}
                  >
                    {day && (
                      <>
                        <span className="day-number">{day}</span>
                        {hasAppointments(day) && (
                          <div className="event-indicators">
                            {appointments[formatDateKey(new Date(currentDate.getFullYear(), currentDate.getMonth(), day))]
                              .slice(0, 3).map((_, i) => (
                                <div key={i} className="event-dot appointment"></div>
                              ))}
                            {appointments[formatDateKey(new Date(currentDate.getFullYear(), currentDate.getMonth(), day))].length > 3 && (
                              <div className="event-more">+{appointments[formatDateKey(new Date(currentDate.getFullYear(), currentDate.getMonth(), day))].length - 3}</div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="calendar-sidebar">
            {/*
            <div className="selected-date-info">
              <h3 className="calendar-sidebar-title">
                {selectedDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </h3>
            </div>
			*/}
            <div className="events-list">
              <h4 className="events-title">Appointments ({getSelectedDateAppointments().length})</h4>
              {loading ? (
                <p className="loading">Loading...</p>
              ) : getSelectedDateAppointments().length === 0 ? (
                <p className="no-events">No appointments scheduled</p>
              ) : (
                <div className="events">
                  {getSelectedDateAppointments().map(appointment => (
                    <div key={appointment.id} className="event-item appointment">
                      <div className="event-header">
                        <h5 className="event-title">{appointment.title}</h5>
                        <div className="appointment-controls">
                          <button className="edit-appointment-btn" onClick={() => handleEditAppointment(appointment)} title="Edit">✏️</button>
                          <button className="delete-event-btn" onClick={() => handleDeleteAppointment(appointment.id)} title="Delete">×</button>
                        </div>
                      </div>
                      <p className="event-time">
                        {formatTimeRange(appointment.start_time || appointment.time, appointment.end_time)}
                        {appointment.duration_minutes && (
                          <span className="duration"> ({getDurationText(appointment.duration_minutes)})</span>
                        )}
                      </p>
                      <div className="client-info">
                        <p className="client-name"><strong>Customer:</strong> {appointment.client.name}</p>
                        {appointment.client.ic && <p className="client-ic-display"><strong>IC:</strong> {appointment.client.ic}</p>}
                        <p className="client-phone"><strong>Phone:</strong> {appointment.client.phone}</p>
                      </div>
                      {appointment.doctor && (
                        <div className="doctor-info">
                          <p className="doctor-name"><strong>Consultant:</strong> {appointment.doctor.name}</p>
                          {appointment.doctor.position && (
                            <p className="doctor-position"><strong>Position:</strong> {appointment.doctor.position}</p>
                          )}
                        </div>
                      )}
                      {appointment.description && <p className="event-description">{appointment.description}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Add Appointment Modal */}
        {showAppointmentModal && (
          <div className="cal-modal-overlay">
            <div className="event-modal">
              <div className="modal-header">
                <h3>Add New Appointment</h3>
                <button className="close-modal-btn" onClick={() => { setShowAppointmentModal(false); resetModal(); }}>×</button>
              </div>
              <div className="modal-body">
                {renderAppointmentForm()}
              </div>
              <div className="modal-footer">
                <button className="cancel-btn" onClick={() => { setShowAppointmentModal(false); resetModal(); }}>Cancel</button>
                <button className="save-btn" onClick={handleSaveAppointment} disabled={loading}>
                  {loading ? 'Saving...' : 'Save Appointment'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Appointment Modal */}
        {showEditModal && (
          <div className="cal-modal-overlay">
            <div className="event-modal">
              <div className="modal-header">
                <h3>Edit Appointment</h3>
                <button className="close-modal-btn" onClick={() => { setShowEditModal(false); resetModal(); }}>×</button>
              </div>
              <div className="modal-body">
                {renderAppointmentForm()}
              </div>
              <div className="modal-footer">
                <button className="cancel-btn" onClick={() => { setShowEditModal(false); resetModal(); }}>Cancel</button>
                <button className="save-btn" onClick={handleUpdateAppointment} disabled={loading}>
                  {loading ? 'Updating...' : 'Update Appointment'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default CalendarPage;